import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge, Loading, ErrorBox } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { CommentsBox } from "@/components/common/CommentsBox";
import { ProposalDocument } from "@/components/proposal/ProposalDocument";
import { AcceptanceRecord } from "@/pages/ProposalDetail";
import { fmtDate } from "@/lib/format";

const CHECKS = [["accept_price", "Acepto el precio de la opción seleccionada"], ["accept_scope", "Acepto el alcance y los entregables"],
  ["accept_renewals", "Acepto las condiciones de renovación"], ["accept_period", "Acepto el periodo contratado"],
  ["accept_email_contact", "Autorizo el contacto por correo electrónico (opcional)"]];
const OPEN = ["Enviada", "Vista por el cliente", "Cambios solicitados"];

function AcceptForm({ p, selected, onDone }) {
  const { user } = useAuth();
  const [f, setF] = useState({ full_name: user.name, email: user.email, comments: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!selected) return setErr("Seleccione una opción de la propuesta.");
    if (!CHECKS.slice(0, 4).every(([k]) => f[k])) return setErr("Debe marcar las cuatro casillas obligatorias.");
    setBusy(true);
    try {
      await api.post(`/proposals/${p.id}/accept`, { ...f, option_key: selected, version: p.current_version });
      toast.success("Propuesta aceptada");
      onDone();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="acceptance-form">
      <p className="text-sm text-slate-600">Opción seleccionada: <b data-testid="selected-option-label">{p.versions.at(-1).options.find((o) => o.key === selected)?.name || "ninguna"}</b> · versión {p.current_version}</p>
      <div><label className="field-label">Nombre completo *</label><input className="field-input" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="accept-name-input" /></div>
      <div><label className="field-label">Correo *</label><input type="email" className="field-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="accept-email-input" /></div>
      {CHECKS.map(([k, l]) => (
        <label key={k} className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" className="mt-0.5 accent-blue-700" checked={Boolean(f[k])} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`accept-${k}`} />{l}</label>
      ))}
      <div><label className="field-label">Comentarios</label><textarea className="field-input" rows={2} value={f.comments} onChange={(e) => setF({ ...f, comments: e.target.value })} data-testid="accept-comments-input" /></div>
      {err && <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="accept-error">{err}</p>}
      <Button type="submit" className="w-full" disabled={busy} data-testid="accept-proposal-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Aceptar propuesta</Button>
    </form>
  );
}

export default function PortalProposal() {
  const { id } = useParams();
  const { data: p, error, reload } = useApi(`/proposals/${id}`);
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState(null);
  useEffect(() => {
    if (p && !selected) {
      const opts = p.versions.at(-1).options;
      setSelected(p.acceptance?.option_key || (opts.length === 1 ? opts[0].key : null));
    }
  }, [p, selected]);
  if (error) return <ErrorBox text={error} />;
  if (!p) return <Loading />;
  const open = OPEN.includes(p.status);
  const current = { ...p, versions: [p.versions.at(-1)] };

  return (
    <div>
      <PageHeader back="/portal/propuestas" title={p.title} subtitle={`${p.number} · versión ${p.current_version} · vigente hasta ${fmtDate(p.valid_until)}`}
        actions={<Button variant="outline" className="no-print" onClick={() => downloadFile(`/proposals/${p.id}/pdf`, null, `${p.number}.pdf`).catch((e) => toast.error(errMsg(e)))} data-testid="portal-download-proposal"><Download className="mr-2 h-4 w-4" />Descargar PDF</Button>} />
      <div className="no-print mb-4"><StatusBadge status={p.status} testid="portal-proposal-status" /></div>
      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <ProposalDocument proposal={current} selectable={open} selected={selected} onSelect={setSelected} />
        <div className="no-print space-y-6">
          {open ? (
            <Section title="Aceptación digital">
              <AcceptForm p={p} selected={selected} onDone={reload} />
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setDecision("request-changes")} data-testid="request-changes-button">Solicitar cambios</Button>
                <Button variant="outline" className="text-rose-700" onClick={() => setDecision("reject")} data-testid="reject-proposal-button">Rechazar</Button>
              </div>
            </Section>
          ) : <Section title="Estado"><p className="text-sm text-slate-600">Esta propuesta está <b>{p.status}</b>.</p></Section>}
          <AcceptanceRecord a={p.acceptance} />
          <Section title="Comentarios">
            <CommentsBox testid="portal-proposal-comments" comments={p.comments} onAdd={async (text) => { await api.post(`/proposals/${id}/comments`, { text }); reload(); }} />
          </Section>
        </div>
      </div>
      <FormDialog open={Boolean(decision)} onOpenChange={(o) => !o && setDecision(null)} testid="decision-form"
        title={decision === "reject" ? "Rechazar propuesta" : "Solicitar cambios"} submitLabel={decision === "reject" ? "Rechazar" : "Enviar solicitud"}
        fields={[{ name: "comments", label: decision === "reject" ? "Motivo" : "¿Qué cambios necesita?", type: "textarea", required: true, minLength: 3 }]}
        onSubmit={async (v) => { await api.post(`/proposals/${id}/${decision}`, v); toast.success("Respuesta enviada"); reload(); }} />
    </div>
  );
}
